import { CardDef, CardInstance, CharacterCardDef, ActionCardDef, getAbilityIcon } from '../types.js';
import { getAbilityDescription, getActionDescription } from '../engine/EffectText.js';

/** Live per-card snapshot consumed by the tooltip handler. With keyed
 *  reconciliation, card DOM nodes are reused across renders — the
 *  pointerenter listener attached at create time captures whatever
 *  `instance` was current then, but the engine clones CardInstance
 *  objects on every dispatch (see `cloneState`), so that captured
 *  reference goes stale immediately. We mirror the latest values onto
 *  this WeakMap and have the tooltip handler read through, so the
 *  tooltip always reflects the live instance state (exhausted/fresh,
 *  effective power, etc.). Keyed by the card element so entries
 *  disappear automatically when the element is GC'd. */
const cardTooltipData = new WeakMap<HTMLElement, {
  instance?: CardInstance;
  effectivePower?: number;
}>();

function setCardTooltipData(el: HTMLElement, instance?: CardInstance, effectivePower?: number): void {
  cardTooltipData.set(el, { instance, effectivePower });
}

const RARITY_EMOJI: Record<string, string> = {
  common: '',
  uncommon: '',
  rare: '',
  legendary: '',
};

// Placeholder art: first letter of each word
function getPlaceholderInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 3);
}

export function createCardElement(
  def: CardDef,
  instance?: CardInstance,
  faceDown = false,
  effectivePower?: number,
  inRing = false,
  tooltipDelayMs = 0,
  lazyLoadArt = false,
  suppressTooltip = false,
  deferImage = false,
): HTMLElement {
  const el = document.createElement('div');
  el.classList.add('card');
  el.dataset.cardId = def.id;

  if (instance) {
    el.dataset.instanceId = instance.instanceId;
    // "Locked" is exhausted + skipNextRefresh — the character will stay
    // exhausted through their owner's next refresh. We render it as a
    // distinct status (🔒 icon, frost overlay) instead of stacking the
    // normal 💤 exhausted icon on top, since the lock is a stronger
    // state and the player needs to see that next-turn refresh won't
    // help.
    const locked = instance.isExhausted && instance.skipNextRefresh;
    if (locked) el.classList.add('locked');
    else if (instance.isExhausted) el.classList.add('exhausted');
    if (instance.isFresh) el.classList.add('fresh');
  }
  // Seed the live snapshot used by the tooltip handler below. The
  // pointerenter listener captured `instance` by closure would go
  // stale across re-renders (cloneState makes a fresh CardInstance on
  // every dispatch); updateCardElement keeps this WeakMap entry
  // current so the tooltip reads through to the latest values.
  setCardTooltipData(el, instance, effectivePower);

  // Block the browser's native long-press / right-click menu over the
  // entire card. Without this, holding on a card (which is the natural
  // mobile gesture for "show me more") triggers Chrome's image context
  // menu and dismisses the tooltip. Cheap to add to every card; the
  // listener does nothing else.
  el.addEventListener('contextmenu', (e) => e.preventDefault());

  if (faceDown) {
    el.classList.add('face-down');
    return el;
  }

  el.dataset.rarity = def.rarity;

  if (def.type === 'character') {
    el.innerHTML = buildCharacterCard(def, instance, effectivePower, inRing, lazyLoadArt, deferImage);
  } else {
    el.innerHTML = buildActionCard(def, lazyLoadArt, deferImage);
  }

  // Tooltip on hover / touch. With tooltipDelayMs > 0, defer showTooltip until
  // the cursor has rested on the card for that long — keeps fast pointer
  // sweeps (e.g. scanning a grid in the deck builder) from triggering a
  // cascade of tooltip flashes. With suppressTooltip, the listeners are
  // skipped entirely (e.g. deck builder uses a tap-to-modal flow instead).
  if (!suppressTooltip) {
    let pendingShow: ReturnType<typeof setTimeout> | null = null;
    const cancelPendingShow = () => {
      if (pendingShow) { clearTimeout(pendingShow); pendingShow = null; }
    };
    // Read live instance + effectivePower from the WeakMap at show
    // time. `def` is stable per instance (a card's identity doesn't
    // change while it's alive) so capturing it by closure is fine.
    const readLive = (): { instance?: CardInstance; effectivePower?: number } =>
      cardTooltipData.get(el) ?? { instance, effectivePower };
    el.addEventListener('pointerenter', (e) => {
      if (tooltipDelayMs > 0 && e.pointerType !== 'touch') {
        cancelPendingShow();
        const ev = e;
        pendingShow = setTimeout(() => {
          pendingShow = null;
          const live = readLive();
          showTooltip(ev, def, live.instance, live.effectivePower);
        }, tooltipDelayMs);
      } else {
        const live = readLive();
        showTooltip(e, def, live.instance, live.effectivePower);
      }
    });
    el.addEventListener('pointerleave', (e) => {
      cancelPendingShow();
      if (e.pointerType === 'touch') scheduleHide(TOUCH_HIDE_DELAY_MS);
      else hideTooltip();
    });
    el.addEventListener('pointermove', moveTooltip);
  }

  return el;
}

function buildCharacterCard(def: CharacterCardDef, instance?: CardInstance, effectivePower?: number, inRing = false, lazyLoadArt = false, deferImage = false): string {
  const power = effectivePower ?? (instance ? instance.currentPower : def.power);
  // A character whose ability has been zeroed out (currently only set
  // by the passive-rebirth resurrection path) renders blank — no icon
  // in the corner, no ability text in the card body. The CardDef's
  // `ability` is still intact (for art / tooltips elsewhere where the
  // canonical card is shown), but on-board the player should see what
  // the engine sees: a stat-only body with nothing to read.
  const abilityActive = !!def.ability && !instance?.abilityDisabled;
  const abilityDesc = abilityActive ? getAbilityDescription(def.ability!) : '';
  const abilityIcon = abilityActive ? `<span class="card-ability-icon" title="${abilityDesc}">${getAbilityIcon(def.ability!.type)}</span>` : '';
  const supportCount = instance && instance.supports.length > 0
    ? `<span class="support-count">+${instance.supports.length}</span>` : '';
  const initials = getPlaceholderInitials(def.name);

  const lazyAttrs = lazyLoadArt ? ' loading="lazy" decoding="async"' : '';
  // `draggable="false"` keeps the browser from showing its native image-drag
  // affordance, which on Android Chrome chains into the long-press image
  // context menu ("Open in new tab" / "Save image" / etc.). Combined with
  // CSS `-webkit-touch-callout: none` and `pointer-events: none` on the
  // img, long-press now reliably falls through to the card and keeps the
  // tooltip up instead of popping the browser menu.
  //
  // `deferImage`: omit the <img> entirely and show the placeholder. The
  // caller (deck-builder collection grid) will inject an <img> later via
  // the throttled image loader once the tile is on-screen.
  const artHtml = !def.art || deferImage
    ? `<span class="placeholder-art">${initials}</span>`
    : `<img src="${def.art}" alt="${def.name}" draggable="false"${lazyAttrs} onerror="this.style.display='none';this.nextElementSibling.style.display=''" /><span class="placeholder-art" style="display:none">${initials}</span>`;

  // Show ability text on card body:
  // - In hand: show all abilities (on-play, passive, activated)
  // - In ring: only show passive and activated (on-play already fired)
  // - Disabled (via `abilityDisabled` flag): never shown.
  const showAbilityOnCard = abilityActive && (!inRing || def.ability!.type !== 'on-play');
  const abilityHtml = showAbilityOnCard
    ? `<div class="card-ability-text">${getAbilityIcon(def.ability!.type)} ${abilityDesc}</div>`
    : '';

  const armorHtml = instance && instance.armor > 0
    ? `<span class="card-armor">🛡${instance.armor}</span>` : '';

  const clockHtml = def.clock !== undefined && def.clock > 1
    ? `<span class="card-clock" title="Clock"><span class="card-clock-icon">⏱</span>${def.clock}</span>` : '';

  // Power lives as a corner-flush badge on the .card (NOT on .card-art),
  // so it visually extends from the card's frame and bumps out over the
  // art. Sibling-of-art means we anchor against the card's outer corner
  // rather than the art's inset corner.
  return `
    ${abilityIcon}
    <div class="card-power-badge">${power}</div>
    <div class="card-art">
      ${artHtml}
      <div class="card-rarity-gem"></div>
    </div>
    <div class="card-name">${def.name}</div>
    ${abilityHtml}
    <div class="card-stats">
      ${clockHtml}
      ${armorHtml}
      <span class="card-spirit">${def.spirit > 0 ? '+' : ''}${def.spirit}<span class="card-spirit-suffix"> SP</span></span>
    </div>
    ${supportCount}
  `;
}

function buildActionCard(def: ActionCardDef, lazyLoadArt = false, deferImage = false): string {
  const badge = def.subtype === 'reaction' ? 'reaction' : 'action';
  const placeholder = badge === 'reaction' ? '🛡' : '⚔';
  const lazyAttrs = lazyLoadArt ? ' loading="lazy" decoding="async"' : '';
  // See note in buildCharacterCard — `draggable="false"` blocks the native
  // long-press image context menu on Android Chrome. `deferImage` mirrors
  // the character-card path: emit only the placeholder; loader will inject
  // the <img> when the tile is actually on-screen.
  const artHtml = !def.art || deferImage
    ? `<span class="placeholder-art">${placeholder}</span>`
    : `<img src="${def.art}" alt="${def.name}" draggable="false"${lazyAttrs} onerror="this.style.display='none';this.nextElementSibling.style.display=''" /><span class="placeholder-art" style="display:none">${placeholder}</span>`;

  const clockHtml = def.clock !== undefined && def.clock > 1
    ? `<span class="card-clock" title="Clock"><span class="card-clock-icon">⏱</span>${def.clock}</span>` : '';

  // Action cards have no power; the bottom row is just clock (left) and
  // spirit (right), matching the character-card layout.
  return `
    <span class="card-type-badge ${badge}">${badge}</span>
    <div class="card-art">${artHtml}<div class="card-rarity-gem"></div></div>
    <div class="card-name">${def.name}</div>
    <div class="card-ability-text">${getActionDescription(def)}</div>
    <div class="card-stats">
      ${clockHtml}
      <span class="card-spirit">${def.spirit > 0 ? '+' : ''}${def.spirit}<span class="card-spirit-suffix"> SP</span></span>
    </div>
  `;
}

function getAbilityTypeName(type: string): string {
  switch (type) {
    case 'on-play': return 'On Play';
    case 'passive': return 'Passive';
    case 'activated': return 'Activated';
    case 'on-attack': return 'On Attack';
    case 'on-attack-alone': return 'On Attack Alone';
    case 'on-damage-opponent': return 'On Damage Opponent';
    case 'end-of-turn': return 'End of Turn';
    case 'beginning-of-turn': return 'Beginning of Turn';
    case 'on-play-character': return 'On Play Character';
    case 'on-play-action': return 'On Play Action';
    default: return type;
  }
}

// ── Tooltip ──

let tooltipEl: HTMLElement | null = null;
let tooltipOwner: HTMLElement | null = null;

const TOUCH_HIDE_DELAY_MS = 1500;
let pendingHide: ReturnType<typeof setTimeout> | null = null;

// When a drag is in flight, tooltips must not show — they'd cover the drop
// targets the player is aiming at. The DragController toggles this around
// pointerdown/pointerup.
let dragSuppressed = false;

export function setDragSuppressed(on: boolean): void {
  dragSuppressed = on;
  if (on) hideTooltip();
}

function cancelPendingHide(): void {
  if (pendingHide) {
    clearTimeout(pendingHide);
    pendingHide = null;
  }
}

function scheduleHide(ms: number): void {
  cancelPendingHide();
  pendingHide = setTimeout(() => { pendingHide = null; hideTooltip(); }, ms);
}

// Document-level watchdog — hide the tooltip when the cursor is no longer over
// the element that opened it, or when that element has been detached from the
// DOM (e.g. a KO'd character in combat). Catches the detach case that
// per-element `mouseleave` misses since `mouseleave` doesn't fire on removed
// nodes. Doesn't depend on any specific class so it works for cards, deck
// entries, collection rows, counter tooltips — anything that calls
// showTooltip / attachTextTooltip.
function installWatchdog(): void {
  if ((installWatchdog as any)._installed) return;
  (installWatchdog as any)._installed = true;
  const check = (target: EventTarget | null) => {
    if (!tooltipEl || !tooltipOwner) return;
    if (!tooltipOwner.isConnected) { hideTooltip(); return; }
    const node = target as Node | null;
    const overOwner = !!node && (node === tooltipOwner || tooltipOwner.contains(node));
    if (!overOwner) hideTooltip();
  };
  document.addEventListener('mousemove', (e) => check(e.target), true);
  document.addEventListener('touchstart', (e) => check(e.target), true);
}

/** Internal: show the card tooltip, owned by `ownerEl`, positioned near
 *  `(clientX, clientY)`. Used by both the normal pointerenter path on a
 *  card and by external callers (DragController) that need to switch the
 *  tooltip to a different card without firing a synthetic mouse event. */
export function showTooltipForCard(
  ownerEl: HTMLElement,
  clientX: number,
  clientY: number,
  def: CardDef,
  instance?: CardInstance,
  effectivePower?: number,
): void {
  if (dragSuppressed) return;
  hideTooltip();
  installWatchdog();
  tooltipOwner = ownerEl;
  tooltipEl = document.createElement('div');
  tooltipEl.classList.add('card-tooltip');

  let html = '';

  if (def.art) {
    html += `<div class="tooltip-art"><img src="${def.art}" alt="${def.name}" onerror="this.parentElement.style.display='none'" /></div>`;
  }

  // Rarity is rendered as a colored dot inline with the name (was a
  // spelled-out word at the end of the stats row).
  html += `<div class="tooltip-name">${def.name} <span class="tooltip-rarity-dot" data-rarity="${def.rarity}" title="${def.rarity}"></span></div>`;

  if (def.type === 'character') {
    const power = effectivePower ?? (instance ? instance.currentPower : def.power);
    const clock = def.clock ?? 1;
    const armorStr = instance && instance.armor > 0 ? `<span style="color:#a0a0b0">Armor: ${instance.armor}</span>` : '';
    html += `<div class="tooltip-stats">
      <span style="color:var(--accent)">Power: ${power}</span>
      ${armorStr}
      <span style="color:#d6b75a">Clock: ${clock}</span>
      <span style="color:var(--spirit-blue)">Spirit: ${def.spirit > 0 ? '+' : ''}${def.spirit}</span>
    </div>`;
    if (def.ability && !instance?.abilityDisabled) {
      html += `<div class="tooltip-ability">
        ${getAbilityIcon(def.ability.type)} ${getAbilityDescription(def.ability)}
      </div>`;
    }
    html += `<div class="tooltip-tags">Tags: ${def.tags.join(', ')}</div>`;

    if (instance) {
      const statuses: string[] = [];
      const locked = instance.isExhausted && instance.skipNextRefresh;
      if (instance.isFresh) statuses.push('Fresh (cannot attack)');
      else if (locked) statuses.push('Locked (cannot attack or block, stays exhausted next turn)');
      else if (instance.isExhausted) statuses.push('Exhausted (cannot attack or block)');
      if (instance.supports.length > 0) statuses.push(`${instance.supports.length} support(s)`);
      if (statuses.length > 0) {
        html += `<div class="tooltip-tags" style="color:var(--accent); margin-top:4px">${statuses.join(' · ')}</div>`;
      }
    }
  } else {
    const clock = def.clock ?? 1;
    html += `<div class="tooltip-stats">
      <span style="color:#d6b75a">Clock: ${clock}</span>
      <span style="color:var(--spirit-blue)">Cost: ${def.spirit}</span>
      <span style="color:var(--text-secondary)">${def.subtype}</span>
    </div>`;
    html += `<div class="tooltip-ability">${getActionDescription(def)}</div>`;
  }

  if (def.flavor) {
    html += `<div class="tooltip-flavor">"${def.flavor}"</div>`;
  }

  tooltipEl.innerHTML = html;
  document.body.appendChild(tooltipEl);
  positionTooltipAt(clientX, clientY);
}

/** Backward-compat shim — the existing pointerenter handlers pass a
 *  MouseEvent. Just unpack and delegate. */
function showTooltip(e: MouseEvent, def: CardDef, instance?: CardInstance, effectivePower?: number): void {
  showTooltipForCard(e.currentTarget as HTMLElement, e.clientX, e.clientY, def, instance, effectivePower);
}

function moveTooltip(e: MouseEvent): void {
  if (tooltipEl) positionTooltipAt(e.clientX, e.clientY);
}

function positionTooltipAt(clientX: number, clientY: number): void {
  if (!tooltipEl) return;
  const rect = tooltipEl.getBoundingClientRect();
  const rightMargin = 48;
  const maxX = window.innerWidth - rect.width - rightMargin;

  let x = clientX + 16;
  // Position above cursor by default
  let y = clientY - rect.height - 16;

  // If tooltip would go above viewport, show below cursor instead
  if (y < 8) {
    y = clientY + 16;
  }

  x = Math.min(x, maxX);
  if (x < 8) x = 8;

  tooltipEl.style.left = `${x}px`;
  tooltipEl.style.top = `${y}px`;
}

export function hideTooltip(): void {
  cancelPendingHide();
  if (tooltipEl) {
    tooltipEl.remove();
    tooltipEl = null;
  }
  tooltipOwner = null;
}

export function attachTooltip(el: HTMLElement, def: CardDef): void {
  el.addEventListener('pointerenter', (e) => showTooltip(e, def));
  el.addEventListener('pointerleave', (e) => {
    if (e.pointerType === 'touch') scheduleHide(TOUCH_HIDE_DELAY_MS);
    else hideTooltip();
  });
  el.addEventListener('pointermove', moveTooltip);
}

export function attachTextTooltip(el: HTMLElement, getText: () => string): void {
  el.addEventListener('pointerenter', (e) => {
    if (dragSuppressed) return;
    hideTooltip();
    installWatchdog();
    tooltipOwner = el;
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add('card-tooltip');
    tooltipEl.innerHTML = `<div class="tooltip-ability">${getText()}</div>`;
    document.body.appendChild(tooltipEl);
    positionTooltipAt(e.clientX, e.clientY);
  });
  el.addEventListener('pointerleave', (e) => {
    if (e.pointerType === 'touch') scheduleHide(TOUCH_HIDE_DELAY_MS);
    else hideTooltip();
  });
  el.addEventListener('pointermove', moveTooltip);
}

/** In-place update of an existing .card element. Companion to
 *  `createCardElement` for the keyed-reconciliation pipeline in
 *  `BoardView.renderBoard`: when an `instanceId` survives a re-render,
 *  we reuse its DOM node and just patch the bits that changed. Writes
 *  are skipped when the target value already matches the current DOM
 *  so spurious CSS transitions don't fire on `transition: all`.
 *
 *  Static-per-instance fields (id, name, rarity, art, ability text,
 *  type-badge) are baked at create time and never touched here. The
 *  `+N` united-bonus overlay is *not* this function's responsibility —
 *  it depends on staging state outside the instance and is reconciled
 *  by the ring loop.
 */
export function updateCardElement(
  el: HTMLElement,
  def: CardDef,
  instance: CardInstance,
  effectivePower?: number,
): void {
  // Keep the tooltip's live snapshot current — see the WeakMap doc
  // at the top of this file for why this matters.
  setCardTooltipData(el, instance, effectivePower);

  // Lock takes priority over plain exhaust visually — see createCardElement
  // for the rationale.
  const locked = instance.isExhausted && instance.skipNextRefresh;
  el.classList.toggle('locked', locked);
  el.classList.toggle('exhausted', instance.isExhausted && !locked);
  el.classList.toggle('fresh', instance.isFresh);

  if (def.type !== 'character') return;

  // Power badge — use effective power (passive bonuses included) if
  // provided (ring path); otherwise fall back to the instance's raw
  // current power (hand path, where passive bonuses don't apply yet).
  const powerText = String(effectivePower ?? instance.currentPower);
  const powerEl = el.querySelector('.card-power-badge');
  if (powerEl && powerEl.textContent !== powerText) {
    powerEl.textContent = powerText;
  }

  // Armor span: insert if armor > 0 and missing, update if text mismatch,
  // remove if armor === 0 and present. Lives inside .card-stats, before
  // the .card-spirit element (matches the create-time order).
  const armorEl = el.querySelector('.card-armor');
  if (instance.armor > 0) {
    const armorText = `🛡${instance.armor}`;
    if (armorEl) {
      if (armorEl.textContent !== armorText) armorEl.textContent = armorText;
    } else {
      const span = document.createElement('span');
      span.className = 'card-armor';
      span.textContent = armorText;
      const statsEl = el.querySelector('.card-stats');
      const spiritEl = el.querySelector('.card-spirit');
      if (statsEl && spiritEl) statsEl.insertBefore(span, spiritEl);
    }
  } else if (armorEl) {
    armorEl.remove();
  }

  // Support count — appears in the corner once the instance has at
  // least one supporting card on top of it.
  const supportEl = el.querySelector('.support-count');
  if (instance.supports.length > 0) {
    const supportText = `+${instance.supports.length}`;
    if (supportEl) {
      if (supportEl.textContent !== supportText) supportEl.textContent = supportText;
    } else {
      const span = document.createElement('span');
      span.className = 'support-count';
      span.textContent = supportText;
      el.appendChild(span);
    }
  } else if (supportEl) {
    supportEl.remove();
  }

  // Ability icon + body text were originally treated as static-per-
  // instance and baked only at create time. The `abilityDisabled`
  // flag (set by passive-rebirth on resurrection) can flip mid-match
  // while the same DOM node lives on through keyed reconciliation,
  // so we strip the icon + body text when the flag goes true. We
  // DON'T synthesize them when the flag is false: the create path
  // may have intentionally omitted the icon (face-down opponent hand
  // cards, etc.), and re-inserting one here would leak the ability
  // icon onto cards that should never show it. abilityDisabled only
  // ever flips one direction (false → true) today, so the strip-only
  // path is sufficient. If a future feature un-disables abilities
  // mid-match, the simpler fix is a forced re-render of the card
  // node instead of patching synthesis in here.
  if (instance.abilityDisabled) {
    el.querySelector('.card-ability-icon')?.remove();
    el.querySelector('.card-ability-text')?.remove();
  }
}
